import { ChevronDown, Play, Square } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface RouteStop {
  index: number;
  clientId: number;
  clientName: string;
  address?: string;
  durationToNextS?: number; // seconds to next stop
  distanceToNextKm?: number; // km to next stop
  isLast: boolean;
  color: string; // Unique color for this stop
  isStart?: boolean; // Is this the start point?
  isEnd?: boolean; // Is this the end point?
  isInRoute?: boolean; // Is this stop within the route range?
}

interface RouteStopsListProps {
  stops: RouteStop[];
  onStopClick?: (stop: RouteStop) => void;
  onIndexClick?: (stop: RouteStop) => void; // Click specifically on the index number
  onSetStart?: (index: number) => void;
  onSetEnd?: (index: number) => void;
  className?: string;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return '<1 мин';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} ч`;
  return `${hours} ч ${remainingMinutes} мин`;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} м`;
  return `${km.toFixed(1)} км`;
}

export function RouteStopsList({ stops, onStopClick, onIndexClick, onSetStart, onSetEnd, className }: RouteStopsListProps) {
  if (stops.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Нет точек для отображения
      </div>
    );
  }

  return (
    <div className={cn('divide-y divide-border', className)}>
      {stops.map((stop) => (
        <div key={stop.clientId} className={cn(!stop.isInRoute && 'opacity-40')}>
          {/* Stop info */}
          <div
            className={cn(
              'px-4 py-3 flex items-start gap-3',
              onStopClick && 'cursor-pointer active:bg-muted/50'
            )}
            onClick={() => onStopClick?.(stop)}
          >
            {/* Index marker with unique color - clickable for zoom */}
            <div
              className={cn(
                "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold text-white shadow-xs",
                onIndexClick && "cursor-pointer active:scale-95 transition-transform"
              )}
              style={{ backgroundColor: stop.color }}
              onClick={(e) => {
                if (onIndexClick) {
                  e.stopPropagation();
                  onIndexClick(stop);
                }
              }}
            >
              {stop.index + 1}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{stop.clientName}</span>
                {stop.isStart && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                    <Play className="w-2.5 h-2.5" />
                    старт
                  </span>
                )}
                {stop.isEnd && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                    <Square className="w-2.5 h-2.5" />
                    стоп
                  </span>
                )}
              </div>
              {stop.address && (
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {stop.address}
                </div>
              )}
            </div>

            {/* Start/Stop buttons */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                className={cn(
                  'p-1 rounded text-[10px] font-medium transition-colors',
                  stop.isStart
                    ? 'bg-green-500 text-white'
                    : 'bg-muted hover:bg-green-100 text-muted-foreground hover:text-green-700'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetStart?.(stop.index);
                }}
                title="Установить как старт"
              >
                <Play className="w-3 h-3" />
              </button>
              <button
                className={cn(
                  'p-1 rounded text-[10px] font-medium transition-colors',
                  stop.isEnd
                    ? 'bg-red-500 text-white'
                    : 'bg-muted hover:bg-red-100 text-muted-foreground hover:text-red-700'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetEnd?.(stop.index);
                }}
                title="Установить как стоп"
              >
                <Square className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Distance/time to next (if not last and in route) */}
          {!stop.isLast && stop.isInRoute && !stop.isEnd && stop.durationToNextS !== undefined && (
            <div className="flex items-center gap-2 px-4 pb-2 text-xs text-muted-foreground">
              <ChevronDown className="w-3 h-3" />
              <span>
                {formatDuration(stop.durationToNextS)}
                {stop.distanceToNextKm !== undefined && (
                  <span className="ml-1">
                    · {formatDistance(stop.distanceToNextKm)}
                  </span>
                )}
              </span>
            </div>
          )}

          {/* End marker for last stop in route */}
          {stop.isEnd && (
            <div className="flex items-center gap-2 px-4 pb-2 text-xs text-red-600 font-medium">
              <Square className="w-3 h-3 ml-7" />
              <span>Конец маршрута</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
